import { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Mic, Square, Loader2, Lock } from "lucide-react";

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read fail"));
    reader.onload = () => {
      const raw = String(reader.result || "");
      const comma = raw.indexOf(",");
      resolve(comma >= 0 ? raw.slice(comma + 1) : raw);
    };
    reader.readAsDataURL(blob);
  });
}

// Browser microphone → Whisper transcription (POST /journal/transcribe).
// Reused by the Journal composer and the Goals wizard. `onTranscribed` gets
// the recognised text to append. `locked` renders a lock chip (voice tier gate).
export default function VoiceRecorderButton({ onTranscribed, lang, disabled, locked, onLockedClick, compact }) {
  const { t } = useI18n();
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mediaRef.current = mr;
      mr.start();
      startedAtRef.current = Date.now();
      setRecording(true);
    } catch {
      toast.error(t("jr_voice_mic_denied"));
    }
  };

  const handleStop = async () => {
    setRecording(false);
    const dur = Date.now() - startedAtRef.current;
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    stopStream();
    if (dur < 800 || blob.size < 200) return;
    setBusy(true);
    try {
      const b64 = await blobToBase64(blob);
      const { data } = await api.post("/journal/transcribe", { audio_base64: b64, audio_format: "webm", lang });
      const txt = (data?.text || "").trim();
      if (txt) { onTranscribed(txt); toast.success(t("journal_transcription_ready")); }
    } catch (e) {
      if (e?.response?.status === 402) toast.error(t("journal_locked_voice"));
      else toast.error(apiError(e, t("journal_transcribing")));
    } finally { setBusy(false); }
  };

  const stop = () => { try { mediaRef.current?.stop(); } catch { /* ignore */ } };

  if (locked) {
    return (
      <button type="button" onClick={onLockedClick} data-testid="voice-locked"
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
        <Lock className="h-3.5 w-3.5" /> {t("journal_locked_voice")}
      </button>
    );
  }

  const label = busy ? t("journal_transcribing") : recording ? t("jr_voice_stop") : t("jr_voice_start");
  return (
    <button type="button" onClick={recording ? stop : start} disabled={disabled || busy} data-testid="voice-record-btn"
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${recording ? "bg-rose-600 text-white" : "bg-primary text-primary-foreground hover:bg-primary/90"} disabled:opacity-50`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : recording ? <Square className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
      {compact ? null : <span>{label}</span>}
    </button>
  );
}
